import { escapeRegex } from "./regex";

const normalizeName = (value?: string | null) => (value ?? "").trim().toLowerCase();

// node__person has no middle-name field; extract one from the free-text title
// by stripping the known first/last name and taking what's left. May be a
// full name ("robert") or just an initial ("r"), depending on what's in the
// title -- letters only, so "R." and "R" both normalize to "r".
const parseMiddleNamePart = (
  fullName?: string | null,
  firstName?: string | null,
  lastName?: string | null,
) => {
  if (!fullName || !firstName || !lastName) return null;
  const remainder = fullName
    .trim()
    .replace(new RegExp(`^${escapeRegex(firstName.trim())}`, "i"), "")
    .replace(new RegExp(`${escapeRegex(lastName.trim())}$`, "i"), "")
    .replace(/[^a-z]/gi, "")
    .toLowerCase();
  return remainder || null;
};

// Compares two middle-name parts at whichever precision both sides support:
// a full name against a full name, or an initial against just the first
// letter of the other side.
const middleNamePartsMatch = (a: string, b: string) =>
  a.length === 1 || b.length === 1 ? a[0] === b[0] : a === b;

// Manual allowlist for name collisions (e.g. "Jacob Taylor" vs "Jacob R.
// Taylor") where a missing middle name can't default to a match. No signal
// in the data identifies which side of a collision needs this, so it's opt-in
// per person rather than inferred from having a middle name at all.
const STRICT_MIDDLE_NAME_PART_NAMES = new Set<string>([
  "jacob r. taylor",
]);

// Gatsby's filter engine resolves each elemMatch sub-field as an independent
// "some element satisfies X" query and intersects the results at the node
// level, rather than requiring one element to satisfy all sub-fields. The
// GraphQL layer can therefore only narrow by last_name; this enforces the
// same-author invariant it can't -- including, for names in
// STRICT_MIDDLE_NAME_PART_NAMES, disambiguating people who share a
// first+last name.
const filterPublicationsByAuthor = (
  publications: JQI.PublicationFeedItemProps[],
  fullName?: string | null,
  firstName?: string | null,
  lastName?: string | null,
) => {
  const targetFirstName = normalizeName(firstName);
  const targetLastName = normalizeName(lastName);
  const targetMiddleNamePart = parseMiddleNamePart(fullName, firstName, lastName);
  const requireExplicitMiddleNamePart = STRICT_MIDDLE_NAME_PART_NAMES.has(normalizeName(fullName));
  return publications.filter((publication) =>
    publication.relationships.authors.some((author) => {
      if (normalizeName(author.firstName) !== targetFirstName) return false;
      if (normalizeName(author.lastName) !== targetLastName) return false;
      if (!targetMiddleNamePart) return true;
      const authorMiddleNamePart = normalizeName(author.middleName).replace(/[^a-z]/gi, "");
      if (requireExplicitMiddleNamePart) {
        return !!authorMiddleNamePart && middleNamePartsMatch(targetMiddleNamePart, authorMiddleNamePart);
      }
      return !authorMiddleNamePart || middleNamePartsMatch(targetMiddleNamePart, authorMiddleNamePart);
    })
  );
};

export {
  filterPublicationsByAuthor,
};
